<template>
  <div>
    <header
      class="fixed left-0 right-0 h-14 bg-brand-600 z-20 lg:hidden flex items-center px-4"
      :style="{ top: offsetTop }"
    >
      <button
        @click="toggleSidebar"
        class="text-white p-2 hover:bg-brand-700 rounded-md transition-colors -ml-2"
        aria-label="Toggle navigation"
      >
        <svg
          v-if="!isSidebarOpen"
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <h1 class="text-white text-lg font-semibold ml-3">IDH Salary Matrix</h1>
    </header>

    <div
      v-if="isSidebarOpen"
      @click="closeSidebar"
      class="fixed top-14 left-0 right-0 bottom-0 bg-black bg-opacity-50 z-20 md:hidden transition-opacity"
    ></div>

    <nav
      id="sidebar-nav"
      :class="[
        'sidebar-nav',
        {
          '-translate-x-full lg:translate-x-0': !isSidebarOpen,
          'translate-x-0': isSidebarOpen,
        },
      ]"
    >
      <!-- Brand -->
      <div class="sidebar-brand px-5 py-6 hidden lg:block lg:relative">
        <h1 class="text-xl font-bold text-white flex items-center gap-x-2">
          <span class="sidebar-brand-short">IDH</span>
          <div class="sidebar-brand-full flex-1 min-w-0">
            <div class="truncate">Salary Matrix</div>
          </div>
        </h1>
      </div>

      <!-- (1) Salary Matrix links (2) Buyer org links -->
      <div class="flex-1">
        <!-- 1. Salary Matrix — same primary nav as regular producer users -->
        <template v-if="showProducerSection">
          <div
            v-if="hasMultipleRoles"
            class="sidebar-section-header px-4 pt-2 lg:pt-0 pb-0"
          >
            <div
              class="sidebar-section-label px-2 text-[11px] font-semibold uppercase tracking-wide text-white/60"
            >
              <span class="sidebar-section-label-full">
                <Translation
                  name="navigation.section_salary_matrix"
                  default="Salary Matrix"
                />
              </span>
              <span class="sidebar-section-label-short" aria-hidden="true"
                >SM</span
              >
            </div>
          </div>

          <div class="sidebar-role-links px-4 space-y-1">
            <a
              :href="dashboardPath"
              :class="navPrimaryClass(isProducerDashboardActive)"
              style="text-decoration: none"
              @click="handleLinkClick"
            >
              <DashboardIcon class="flex-none" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.dashboard"
                    default="Dashboard"
                  />
                </div>
              </div>
            </a>

            <a
              :href="facilitiesPath"
              :class="navPrimaryClass(isFacilitiesActive)"
              style="text-decoration: none"
              data-tour-step="facilities"
              @click="handleLinkClick"
            >
              <FacilityNavIcon class="flex-none" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.facilities"
                    default="Facilities"
                  />
                </div>
              </div>
            </a>

            <a
              :href="wageCalculationsPath"
              :class="navPrimaryClass(isWageCalculationsActive)"
              style="text-decoration: none"
              data-tour-step="wage-calculations"
              @click="handleLinkClick"
            >
              <CalculationIcon class="flex-none" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.wage_calculations"
                    default="Wage Calculations"
                  />
                </div>
              </div>
            </a>

            <a
              v-if="showCollaborationsNav"
              :href="collaborationsPath"
              :class="navPrimaryClass(isCollaborationsActive)"
              style="text-decoration: none"
              @click="handleLinkClick"
            >
              <CollaborationNavIcon class="flex-none" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.collaborations"
                    default="Collaborations"
                  />
                </div>
              </div>
              <span
                v-if="collaborationsNotificationCount > 0"
                class="announcement-badge ml-auto bg-white text-brand-800 text-xs rounded-full px-2 py-1"
              >
                {{ collaborationsNotificationCount }}
              </span>
            </a>

            <a
              v-if="showProducerLwContribution"
              :href="producerSharedLwContributionsPath"
              :class="navPrimaryClass(isProducerSharedLwContributionsActive)"
              style="text-decoration: none"
              @click="handleLinkClick"
            >
              <BuyerNavLwContributionIcon class="h-5 w-5 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.producer_lw_contributions"
                    default="LW Contributions"
                  />
                </div>
              </div>
              <span
                v-if="producerSharedScenariosNotificationCount > 0"
                class="announcement-badge ml-auto shrink-0 rounded-full bg-white px-2 py-1 text-xs font-semibold text-brand-800"
              >
                {{ producerSharedScenariosNotificationCount }}
              </span>
            </a>
          </div>
        </template>

        <div
          v-if="showProducerSection && showBuyerSection"
          class="sidebar-section-divider my-2 border-t border-white/10 mx-4"
        ></div>

        <!-- 2. Buyer — dashboard, supply chain, manage invites, settings (same order as buyer-only sidebar) -->
        <template v-if="showBuyerSection">
          <div v-if="hasMultipleRoles" class="sidebar-section-header px-4 pb-0">
            <div
              class="sidebar-section-label px-2 text-[11px] font-semibold uppercase tracking-wide text-white/60"
            >
              <span class="sidebar-section-label-full">
                <Translation name="navigation.section_buyer" default="Buyer" />
              </span>
              <span class="sidebar-section-label-short" aria-hidden="true"
                >B</span
              >
            </div>
          </div>

          <div class="sidebar-role-links px-4 space-y-1 pb-2">
            <a
              :href="buyerDashboardPath"
              :class="navPrimaryClass(isBuyerDashboardActive)"
              style="text-decoration: none"
              @click="handleLinkClick"
            >
              <BuyerNavDashboardGridIcon class="h-5 w-5 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.buyer_dashboard"
                    default="Buyer's dashboard"
                  />
                </div>
              </div>
            </a>

            <a
              :href="supplyChainPath"
              :class="navPrimaryClass(isSupplyChainActive)"
              style="text-decoration: none"
              @click="handleLinkClick"
            >
              <BuyerNavLinkIcon class="h-5 w-5 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.buyer_supply_chain"
                    default="Supply chain"
                  />
                </div>
              </div>
            </a>

            <a
              :href="manageInvitesPath"
              :class="navPrimaryClass(isManageInvitesActive)"
              style="text-decoration: none"
              @click="handleLinkClick"
            >
              <BuyerNavUsersIcon class="h-5 w-5 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.buyer_manage_invites"
                    default="Manage invites"
                  />
                </div>
              </div>
            </a>

            <a
              v-if="showLwContribution"
              :href="lwContributionPath"
              :class="navPrimaryClass(isLwContributionActive)"
              style="text-decoration: none"
              @click="handleLinkClick"
            >
              <BuyerNavLwContributionIcon class="h-5 w-5 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.buyer_lw_contribution"
                    default="LW Contribution"
                  />
                </div>
              </div>
              <span
                v-if="displayLwContributionNotificationCount > 0"
                class="announcement-badge ml-auto shrink-0 rounded-full bg-white px-2 py-1 text-xs font-semibold text-brand-800"
              >
                {{ displayLwContributionNotificationCount }}
              </span>
            </a>

            <a
              v-if="showSettingsNav"
              :href="settingsPath"
              :class="navPrimaryClass(isSettingsActive)"
              style="text-decoration: none"
              @click="handleLinkClick"
            >
              <BuyerNavCogIcon class="h-5 w-5 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.buyer_settings"
                    default="Settings"
                  />
                </div>
              </div>
            </a>
          </div>
        </template>

        <div
          v-if="
            showIntermediarySection && (showProducerSection || showBuyerSection)
          "
          class="sidebar-section-divider my-2 border-t border-white/10 mx-4"
        ></div>

        <!-- 3. Intermediary — acts on behalf of the buyers they are linked to -->
        <template v-if="showIntermediarySection">
          <div v-if="hasMultipleRoles" class="sidebar-section-header px-4 pb-0">
            <div
              class="sidebar-section-label px-2 text-[11px] font-semibold uppercase tracking-wide text-white/60"
            >
              <span class="sidebar-section-label-full">
                <Translation
                  name="navigation.section_intermediary"
                  default="Intermediary"
                />
              </span>
              <span class="sidebar-section-label-short" aria-hidden="true"
                >I</span
              >
            </div>
          </div>

          <div class="sidebar-role-links px-4 space-y-1 pb-2">
            <a
              :href="intermediaryDashboardPath"
              :class="navPrimaryClass(isIntermediaryDashboardActive)"
              style="text-decoration: none"
              @click="handleLinkClick"
            >
              <BuyerNavDashboardGridIcon class="h-5 w-5 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.intermediary_dashboard"
                    default="Dashboard"
                  />
                </div>
              </div>
            </a>

            <a
              :href="intermediarySupplyChainPath"
              :class="navPrimaryClass(isIntermediarySupplyChainActive)"
              style="text-decoration: none"
              @click="handleLinkClick"
            >
              <BuyerNavLinkIcon class="h-5 w-5 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.buyer_supply_chain"
                    default="Supply chain"
                  />
                </div>
              </div>
            </a>

            <a
              :href="intermediaryManageInvitesPath"
              :class="navPrimaryClass(isIntermediaryManageInvitesActive)"
              style="text-decoration: none"
              @click="handleLinkClick"
            >
              <BuyerNavUsersIcon class="h-5 w-5 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.buyer_manage_invites"
                    default="Manage invites"
                  />
                </div>
              </div>
              <span
                v-if="intermediaryPendingRelationsCount > 0"
                class="announcement-badge ml-auto shrink-0 rounded-full bg-white px-2 py-1 text-xs font-semibold text-brand-800"
              >
                {{ intermediaryPendingRelationsCount }}
              </span>
            </a>

            <a
              v-if="showIntermediaryLwContribution"
              :href="intermediarySharedLwContributionsPath"
              :class="
                navPrimaryClass(isIntermediarySharedLwContributionsActive)
              "
              style="text-decoration: none"
              @click="handleLinkClick"
            >
              <BuyerNavLwContributionIcon class="h-5 w-5 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="truncate">
                  <Translation
                    name="navigation.intermediary_lw_contributions"
                    default="LW Contributions"
                  />
                </div>
              </div>
              <span
                v-if="intermediarySharedScenariosNotificationCount > 0"
                class="announcement-badge ml-auto shrink-0 rounded-full bg-white px-2 py-1 text-xs font-semibold text-brand-800"
              >
                {{ intermediarySharedScenariosNotificationCount }}
              </span>
            </a>
          </div>
        </template>
      </div>

      <!-- 3. Shared utility — same as regular producer sidebar (announcements, guided tour, e-learning, support, profile) -->
      <div class="sidebar-footer px-4 py-6 space-y-1 border-t border-white/10">
        <a
          :href="announcementsPath"
          :class="navPrimaryClass(isAnnouncementsActive)"
          style="text-decoration: none"
          data-turbo="false"
          data-tour-step="announcements"
          @click="handleLinkClick"
        >
          <AnnouncementIcon class="flex-none" />
          <div class="flex-1 min-w-0">
            <div class="truncate">
              <Translation
                name="navigation.announcements"
                default="Announcements"
              />
            </div>
          </div>
          <span
            v-if="displayUnviewedCount > 0"
            class="announcement-badge ml-auto bg-white text-brand-800 text-xs rounded-full px-2 py-1"
          >
            {{ displayUnviewedCount }}
          </span>
        </a>

        <template v-if="isProducerDashboardActive">
          <a
            href="#"
            class="text-white hover:bg-brand-25 hover:bg-opacity-10 nav-link"
            style="text-decoration: none"
            data-tour-step="guided-tour"
            @click.prevent="openGuidedTourModal"
          >
            <GuidedTourIcon class="flex-none" />
            <div class="flex-1 min-w-0">
              <div class="truncate">
                <Translation
                  name="navigation.guided_tour"
                  default="Guided Tour"
                />
              </div>
            </div>
          </a>

          <GuidedTourModal ref="guidedTourModalRef" />

          <GuidedTourTooltip
            v-if="currentStepData"
            :is-visible="isTourActive"
            :title="currentStepData.title"
            :title-key="currentStepData.titleKey"
            :description="currentStepData.description"
            :description-key="currentStepData.descriptionKey"
            :current-step="currentTourStep"
            :total-steps="totalTourSteps"
            :target-element="currentTargetElement"
            @close="closeTour"
            @prev="prevTourStep"
            @next="nextTourStep"
          />
        </template>

        <a
          :href="eLearningPath"
          target="_blank"
          class="text-white hover:bg-brand-25 hover:bg-opacity-10 nav-link"
          style="text-decoration: none"
          data-tour-step="e-learning"
          @click="handleLinkClick"
        >
          <LearningIcon class="flex-none" />
          <div class="flex-1 min-w-0">
            <div class="truncate">
              <Translation name="navigation.e_learning" default="E-Learning" />
            </div>
          </div>
        </a>

        <a
          href="mailto:livingwagematrix@idhtrade.org"
          class="text-white hover:bg-brand-25 hover:bg-opacity-10 nav-link"
          style="text-decoration: none"
        >
          <ChatIcon class="flex-none" />
          <div class="flex-1 min-w-0">
            <div class="truncate">
              <Translation
                name="navigation.support"
                default="Contact Support"
              />
            </div>
          </div>
        </a>

        <div class="relative !mt-6">
          <button
            type="button"
            data-tour-step="profile"
            @click="toggleUserMenu"
            class="prof-button py-3 w-full rounded-lg flex items-start text-left hover:bg-opacity-20 transition-colors"
            style="box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05)"
          >
            <div
              class="h-10 w-10 rounded-full bg-brand-400 flex items-center justify-center flex-none mr-2"
            >
              <span class="text-sm font-semibold text-brand-50">
                {{ userInitials }}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-gray-50 font-semibold text-sm truncate">
                {{ userName }}
              </div>
              <div class="text-white text-opacity-70 text-sm truncate">
                {{ userEmail }}
              </div>
            </div>
            <DropdownArrowsIcon class="ml-auto hidden prof-toggle" />
          </button>
          <div
            v-if="isUserMenuOpen"
            class="prof-menu absolute bottom-full left-0 mb-2 w-full rounded-md shadow-lg bg-white ring-1 ring-black/5"
          >
            <a
              :href="profilePath"
              :class="[
                'block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-md',
                isProfileActive ? 'font-semibold bg-gray-100' : '',
              ]"
            >
              <Translation
                name="navigation.profile_settings"
                default="Profile Settings"
              />
            </a>
            <a
              :href="logoutPath"
              data-turbo-method="delete"
              class="rounded-b-md block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Translation name="navigation.logout" default="Logout" />
            </a>
          </div>
        </div>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import DashboardIcon from "./icons/DashboardIcon.vue";
import FacilityNavIcon from "./icons/FacilityNavIcon.vue";
import CalculationIcon from "./icons/CalculationIcon.vue";
import CollaborationNavIcon from "./icons/CollaborationNavIcon.vue";
import AnnouncementIcon from "./icons/AnnouncementIcon.vue";
import ChatIcon from "./icons/ChatIcon.vue";
import GuidedTourIcon from "./icons/GuidedTourIcon.vue";
import DropdownArrowsIcon from "./icons/DropdownArrowsIcon.vue";
import LearningIcon from "./icons/LearningIcon.vue";
import GuidedTourModal from "./GuidedTourModal.vue";
import GuidedTourTooltip from "./GuidedTourTooltip.vue";
import BuyerNavDashboardGridIcon from "./icons/BuyerNavDashboardGridIcon.vue";
import BuyerNavLinkIcon from "./icons/BuyerNavLinkIcon.vue";
import BuyerNavUsersIcon from "./icons/BuyerNavUsersIcon.vue";
import BuyerNavLwContributionIcon from "./icons/BuyerNavLwContributionIcon.vue";
import BuyerNavCogIcon from "./icons/BuyerNavCogIcon.vue";
import Translation from "./aft/Translation.vue";

const props = defineProps({
  // Which sections to render — a user may hold any subset of the three roles.
  showProducerSection: { type: Boolean, default: true },
  showBuyerSection: { type: Boolean, default: true },
  showIntermediarySection: { type: Boolean, default: false },
  // "intermediary" while viewing the intermediary section; "" otherwise. Needed
  // because buyer and intermediary share the /supply-chain & /manage-invites
  // paths (they differ only by the workspace query param, which currentPath omits).
  currentWorkspace: { type: String, default: "" },
  // Producer paths (Salary Matrix)
  dashboardPath: String,
  facilitiesPath: String,
  wageCalculationsPath: String,
  collaborationsPath: { type: String, default: "#" },
  producerSharedLwContributionsPath: { type: String, default: "#" },
  showProducerLwContribution: { type: Boolean, default: false },
  producerSharedScenariosNotificationCount: { type: Number, default: 0 },
  // Shared paths
  announcementsPath: String,
  eLearningPath: String,
  profilePath: String,
  logoutPath: String,
  currentPath: String,
  /** When set (e.g. "supply_chain"), highlights buyer nav despite producer URL prefix. */
  activeBuyerNav: { type: String, default: "" },
  // Buyer paths
  buyerDashboardPath: String,
  supplyChainPath: { type: String, default: "#" },
  manageInvitesPath: { type: String, default: "#" },
  lwContributionPath: { type: String, default: "#" },
  showLwContribution: { type: Boolean, default: false },
  showSettingsNav: { type: Boolean, default: false },
  settingsPath: String,
  // Chrome
  userName: String,
  userEmail: String,
  userInitials: String,
  offsetTop: { type: Number, default: 0 },
  unviewedCount: { type: Number, default: 0 },
  collaborationsNotificationCount: { type: Number, default: 0 },
  lwContributionNotificationCount: { type: Number, default: 0 },
  showCollaborationsNav: { type: Boolean, default: false },
  guidedTourShown: { type: Boolean, default: false },
  // Intermediary paths + badges
  intermediaryDashboardPath: { type: String, default: "#" },
  intermediarySupplyChainPath: { type: String, default: "#" },
  intermediaryManageInvitesPath: { type: String, default: "#" },
  intermediarySharedLwContributionsPath: { type: String, default: "#" },
  showIntermediaryLwContribution: { type: Boolean, default: false },
  // Pending buyer invitations plus unviewed producer accept/decline responses.
  intermediaryPendingRelationsCount: { type: Number, default: 0 },
  intermediarySharedScenariosNotificationCount: { type: Number, default: 0 },
});

const isSidebarOpen = ref(false);
const isUserMenuOpen = ref(false);
const announcementsViewed = ref(false);
const lwContributionUpdatesViewed = ref(false);
const guidedTourModalRef = ref(null);

const isTourActive = ref(false);
const currentTourStep = ref(1);
const currentTargetElement = ref(null);

const tourSteps = [
  {
    targetSelector: '[data-tour-step="facilities"]',
    title: "Facilities",
    titleKey: "guided_tour.step_facilities_title",
    description:
      "Tooltips are used to describe or identify an element. In most scenarios, tooltips help the user understand meaning, function or alt-text.",
    descriptionKey: "guided_tour.step_facilities_description",
  },
  {
    targetSelector: '[data-tour-step="wage-calculations"]',
    title: "Wage Calculations",
    titleKey: "guided_tour.step_wage_calculations_title",
    description:
      "Tooltips are used to describe or identify an element. In most scenarios, tooltips help the user understand meaning, function or alt-text.",
    descriptionKey: "guided_tour.step_wage_calculations_description",
  },
  {
    targetSelector: '[data-tour-step="profile"]',
    title: "Profile Settings",
    titleKey: "guided_tour.step_profile_title",
    description:
      "Tooltips are used to describe or identify an element. In most scenarios, tooltips help the user understand meaning, function or alt-text.",
    descriptionKey: "guided_tour.step_profile_description",
  },
  {
    targetSelector: '[data-tour-step="announcements"]',
    title: "Announcements",
    titleKey: "guided_tour.step_announcements_title",
    description:
      "Tooltips are used to describe or identify an element. In most scenarios, tooltips help the user understand meaning, function or alt-text.",
    descriptionKey: "guided_tour.step_announcements_description",
  },
  {
    targetSelector: '[data-tour-step="e-learning"]',
    title: "E-Learning",
    titleKey: "guided_tour.step_e_learning_title",
    description:
      "Tooltips are used to describe or identify an element. In most scenarios, tooltips help the user understand meaning, function or alt-text.",
    descriptionKey: "guided_tour.step_e_learning_description",
  },
];

function pathMatches(current, target) {
  if (!target || target === "#") return false;
  return (
    current === target || (target.length > 1 && current.startsWith(target))
  );
}

const displayUnviewedCount = computed(() => {
  if (announcementsViewed.value) return 0;
  return props.unviewedCount;
});

const displayLwContributionNotificationCount = computed(() => {
  if (lwContributionUpdatesViewed.value) return 0;
  return props.lwContributionNotificationCount;
});

// Group labels (Salary Matrix / Buyer / Intermediary) only make sense when the
// user holds more than one role; a single-role user sees just their own links.
const hasMultipleRoles = computed(
  () =>
    [
      props.showProducerSection,
      props.showBuyerSection,
      props.showIntermediarySection,
    ].filter(Boolean).length > 1,
);

// Buyer and intermediary share /supply-chain & /manage-invites, distinguished
// only by the workspace query param. currentWorkspace tells us which side is live.
const isIntermediaryWorkspace = computed(
  () => props.currentWorkspace === "intermediary",
);
function baseOf(path) {
  return (path || "").split("?")[0];
}

/** Salary Matrix dashboard (`/dashboard`) — producer landing; not the buyer/intermediary area. */
const isProducerDashboardActive = computed(
  () =>
    !isIntermediaryWorkspace.value && props.currentPath === props.dashboardPath,
);

const isFacilitiesActive = computed(() =>
  pathMatches(props.currentPath, props.facilitiesPath),
);
const isWageCalculationsActive = computed(() => {
  if (props.activeBuyerNav === "supply_chain") return false;
  return pathMatches(props.currentPath, props.wageCalculationsPath);
});
const isCollaborationsActive = computed(() =>
  pathMatches(props.currentPath, props.collaborationsPath),
);
const isProducerSharedLwContributionsActive = computed(
  () =>
    !isIntermediaryWorkspace.value &&
    pathMatches(
      props.currentPath,
      baseOf(props.producerSharedLwContributionsPath),
    ),
);
const isBuyerDashboardActive = computed(
  () =>
    !isIntermediaryWorkspace.value &&
    pathMatches(props.currentPath, props.buyerDashboardPath),
);
const isSupplyChainActive = computed(() => {
  if (isIntermediaryWorkspace.value) return false;
  if (props.activeBuyerNav === "supply_chain") return true;
  return pathMatches(props.currentPath, props.supplyChainPath);
});
const isManageInvitesActive = computed(
  () =>
    !isIntermediaryWorkspace.value &&
    pathMatches(props.currentPath, props.manageInvitesPath),
);
const isLwContributionActive = computed(() =>
  pathMatches(props.currentPath, props.lwContributionPath),
);
const isSettingsActive = computed(() =>
  pathMatches(props.currentPath, props.settingsPath),
);

const isIntermediaryDashboardActive = computed(
  () =>
    isIntermediaryWorkspace.value &&
    props.currentPath === baseOf(props.intermediaryDashboardPath),
);
const isIntermediarySupplyChainActive = computed(
  () =>
    isIntermediaryWorkspace.value &&
    pathMatches(props.currentPath, baseOf(props.intermediarySupplyChainPath)),
);
const isIntermediaryManageInvitesActive = computed(
  () =>
    isIntermediaryWorkspace.value &&
    pathMatches(props.currentPath, baseOf(props.intermediaryManageInvitesPath)),
);
const isIntermediarySharedLwContributionsActive = computed(
  () =>
    isIntermediaryWorkspace.value &&
    pathMatches(
      props.currentPath,
      baseOf(props.intermediarySharedLwContributionsPath),
    ),
);
const isAnnouncementsActive = computed(
  () => props.currentPath === props.announcementsPath,
);
const isProfileActive = computed(() => props.currentPath === props.profilePath);

const currentStepData = computed(
  () => tourSteps[currentTourStep.value - 1] || null,
);
const totalTourSteps = computed(() => tourSteps.length);

function navPrimaryClass(active) {
  return [
    "nav-link",
    active
      ? "text-white bg-brand-700"
      : "text-white hover:bg-brand-25 hover:bg-opacity-10",
  ];
}

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
  document.body.style.overflow = isSidebarOpen.value ? "hidden" : "";
};

const closeSidebar = () => {
  isSidebarOpen.value = false;
  document.body.style.overflow = "";
};

const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
};

const handleLinkClick = () => {
  if (window.innerWidth < 1024) closeSidebar();
};

const openGuidedTourModal = () => {
  if (window.innerWidth < 1024) closeSidebar();
  guidedTourModalRef.value?.openModal();
};

const startTour = () => {
  if (window.innerWidth < 1024) {
    isSidebarOpen.value = true;
    document.body.style.overflow = "hidden";
  }

  const sidebar = document.getElementById("sidebar-nav");
  if (sidebar) {
    sidebar.classList.add("expanded");
  }

  setTimeout(() => {
    isTourActive.value = true;
    currentTourStep.value = 1;
    updateTargetElement();
  }, 300);
};

const updateTargetElement = () => {
  const stepData = tourSteps[currentTourStep.value - 1];
  if (stepData) {
    if (stepData.targetSelector === '[data-tour-step="profile"]') {
      isUserMenuOpen.value = true;
      setTimeout(() => {
        currentTargetElement.value = document.querySelector(
          stepData.targetSelector,
        );
      }, 50);
    } else {
      isUserMenuOpen.value = false;
      currentTargetElement.value = document.querySelector(
        stepData.targetSelector,
      );
    }
  }
};

const closeTour = () => {
  isTourActive.value = false;
  currentTourStep.value = 1;
  currentTargetElement.value = null;
  isUserMenuOpen.value = false;

  if (window.innerWidth < 1024) {
    closeSidebar();
  }
};

const prevTourStep = () => {
  if (currentTourStep.value > 1) {
    currentTourStep.value--;
    updateTargetElement();
  }
};

const nextTourStep = () => {
  if (currentTourStep.value < tourSteps.length) {
    currentTourStep.value++;
    updateTargetElement();
  } else {
    closeTour();
  }
};

const handleTourStart = () => {
  startTour();
};

const handleResize = () => {
  if (window.innerWidth >= 1024) closeSidebar();
};

const handleClickOutside = (event) => {
  if (isUserMenuOpen.value && !event.target.closest(".prof-button")) {
    isUserMenuOpen.value = false;
  }
};

const handleEscape = (event) => {
  if (event.key === "Escape") {
    if (isSidebarOpen.value) closeSidebar();
    if (isUserMenuOpen.value) isUserMenuOpen.value = false;
  }
};

const handleAnnouncementsViewed = () => {
  announcementsViewed.value = true;
};

const markGuidedTourShown = async () => {
  try {
    const locale = window.location.pathname.split("/")[1] || "en";
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");

    if (!csrfToken) {
      console.error("CSRF token not found - cannot mark guided tour as shown");
      return;
    }

    const response = await fetch(`/${locale}/profile/guided_tour_shown`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "application/json",
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        "Failed to mark guided tour as shown:",
        response.status,
        errorData,
      );
    }
  } catch (error) {
    console.error("Failed to mark guided tour as shown:", error);
  }
};

const handleGuidedTourModalClose = () => {
  if (!props.guidedTourShown) {
    markGuidedTourShown();
  }
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleEscape);
  window.addEventListener("announcements:viewed", handleAnnouncementsViewed);
  window.addEventListener("guided-tour:start", handleTourStart);
  window.addEventListener("guided-tour:closed", handleGuidedTourModalClose);

  const sidebar = document.getElementById("sidebar-nav");
  if (sidebar) {
    sidebar.addEventListener("mouseenter", () => {
      if (!isTourActive.value) {
        sidebar.classList.add("expanded");
        localStorage.setItem("sidebarExpanded", "true");
      }
    });

    sidebar.addEventListener("mouseleave", () => {
      if (!isTourActive.value) {
        sidebar.classList.remove("expanded");
        localStorage.removeItem("sidebarExpanded");
        isUserMenuOpen.value = false;
      }
    });

    if (localStorage.getItem("sidebarExpanded")) {
      sidebar.classList.add("expanded");
    }
  }

  if (!props.guidedTourShown) {
    setTimeout(() => {
      guidedTourModalRef.value?.openModal();
    }, 500);
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleEscape);
  window.removeEventListener("announcements:viewed", handleAnnouncementsViewed);
  window.removeEventListener("guided-tour:start", handleTourStart);
  window.removeEventListener("guided-tour:closed", handleGuidedTourModalClose);
  document.body.style.overflow = "";
});
</script>
